import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Bot, ArrowUpRight } from "lucide-react";
import { SOCIALS, PHOTOS } from "../data/mock";

const GRID = [PHOTOS.bhutan, PHOTOS.livingRoom, PHOTOS.golf, PHOTOS.christmas, PHOTOS.selfie];

export const Footer = () => {
  return (
    <footer
      data-testid="footer-section"
      style={{
        background: "var(--brand-cream)",
        borderTop: "1px solid var(--brand-line)",
      }}
    >
      <div className="py-24 md:py-32 px-6 md:px-12 lg:px-20 text-center">
        <h2
          className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto"
          style={{ color: "var(--brand-ink)" }}
          data-testid="footer-cta-heading"
        >
          If you have any questions, reach out.
        </h2>
        <p
          className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
          style={{ color: "var(--brand-muted)" }}
        >
          I'd genuinely love to hear from you, questions, guest pitches, or
          just a hello. My DMs are always open, and I read every single one.
        </p>
        <a
          href={SOCIALS.instagramDM}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-get-in-touch-button"
          className="mt-9 inline-flex items-center gap-2.5 rounded-full btn-primary font-semibold px-8 py-4"
        >
          <Instagram size={18} strokeWidth={2} />
          Get in touch
        </a>
      </div>

      {/* Dual social CTAs, Instagram & YouTube as equal citizens */}
      <div className="px-6 md:px-12 lg:px-20 pb-14" data-testid="footer-social-ctas">
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <a
            href={SOCIALS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-cta"
            className="group flex items-center justify-between gap-4 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
            style={{
              background: "var(--brand-cream-soft)",
              border: "1px solid var(--brand-line)",
            }}
          >
            <div className="flex items-center gap-4">
              <span
                className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                style={{
                  background:
                    "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 85%,#515BD4 100%)",
                }}
              >
                <Instagram size={20} strokeWidth={2} />
              </span>
              <div className="text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-muted)" }}>
                  Instagram
                </p>
                <p className="text-lg font-bold" style={{ color: "var(--brand-ink)" }}>
                  Follow @hunarsyal
                </p>
              </div>
            </div>
            <ArrowUpRight
              size={20}
              strokeWidth={1.8}
              style={{ color: "var(--brand-muted)" }}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </a>
          <a
            href={SOCIALS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-youtube-cta"
            className="group flex items-center justify-between gap-4 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
            style={{
              background: "var(--brand-cream-soft)",
              border: "1px solid var(--brand-line)",
            }}
          >
            <div className="flex items-center gap-4">
              <span
                className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                style={{ background: "#FF0000" }}
              >
                <Youtube size={22} strokeWidth={2} />
              </span>
              <div className="text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-muted)" }}>
                  YouTube
                </p>
                <p className="text-lg font-bold" style={{ color: "var(--brand-ink)" }}>
                  Subscribe to Hunar Syal
                </p>
              </div>
            </div>
            <ArrowUpRight
              size={20}
              strokeWidth={1.8}
              style={{ color: "var(--brand-muted)" }}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </a>
        </div>
      </div>

      {/* Instagram photo grid */}
      <div className="px-6 md:px-12 lg:px-20 pb-16" data-testid="footer-instagram-grid">
        <a
          href={SOCIALS.instagram}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-instagram-handle-link"
          className="inline-flex items-center gap-2 font-semibold hover:opacity-80 transition-opacity duration-300 mb-6"
          style={{ color: "var(--brand-accent)" }}
        >
          <Instagram size={18} strokeWidth={2} />
          @hunarsyal
        </a>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {GRID.map((src, i) => (
            <a
              key={i}
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`footer-grid-photo-${i + 1}`}
              className="group relative aspect-square rounded-xl overflow-hidden"
            >
              <img
                src={src}
                alt="Hunar Syal on Instagram"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
              />
            </a>
          ))}
          <a
            href={SOCIALS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-grid-follow-tile"
            className="flex flex-col items-center justify-center gap-2 aspect-square rounded-xl btn-primary"
          >
            <Instagram size={24} strokeWidth={1.8} />
            <span className="text-xs font-semibold">Follow</span>
          </a>
        </div>
      </div>

      <div
        className="px-6 md:px-12 lg:px-20 py-7 flex flex-col md:flex-row items-center justify-between gap-4"
        style={{ borderTop: "1px solid var(--brand-line)" }}
      >
        <p className="text-sm" style={{ color: "var(--brand-muted)" }}>
          © 2026 Hunar Syal. All rights reserved.
        </p>
        <div className="flex flex-wrap items-center gap-5">
          {/* AI/LLM friendly view, cleanly separated so machines can find the plain-text index */}
          <Link
            to="/llms"
            data-testid="footer-llms-link"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full transition-colors duration-300"
            style={{
              border: "1px solid var(--brand-line)",
              color: "var(--brand-muted)",
            }}
            title="Plain-text view for AI, LLMs & assistants"
          >
            <Bot size={13} strokeWidth={2} />
            For AI & LLMs
          </Link>
          <div className="flex items-center gap-5" style={{ color: "var(--brand-muted)" }}>
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" aria-label="Instagram" className="hover:text-[var(--brand-accent)] transition-colors duration-300">
              <Instagram size={19} strokeWidth={1.8} />
            </a>
            <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" data-testid="footer-youtube-link" aria-label="YouTube" className="hover:text-[var(--brand-accent)] transition-colors duration-300">
              <Youtube size={21} strokeWidth={1.8} />
            </a>
            <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link" aria-label="LinkedIn" className="hover:text-[var(--brand-accent)] transition-colors duration-300">
              <Linkedin size={19} strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
